import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { keyframes } from '@emotion/react';
import { decodeIdToken } from '../utils/token.utils';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/auth.service';
import { API_ENDPOINTS } from '../constants/endpoints';
import axios from 'axios';
import StorageService, { STORAGE_KEYS } from '../utils/storage.util';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20px);
  }
`;

const loadingMessages = [
    'Verifying Your Account',
    'Setting Up Your Session',
    'All Set!',
];
const subheadings = [
    `We're securely matching your credentials with our records.`,
    `We're preparing a personalized experience based on your account history.`,
    'Your dashboard is ready. Thank you for your patience!'
];

const LinkAccount = () => {
    const [verifying, setVerifying] = useState(false);
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const [currentSubheadingIndex, setCurrentSubheadingIndex] = useState(0);
    const [email, setEmail] = useState('');
    const [userId, setUserId] = useState('');
    const navigate = useNavigate();
    const hasProcessedUnload = useRef(false);

    useEffect(() => {
        // Get email and user ID from decoded id token
        const decodedToken = decodeIdToken();
        if (decodedToken) {
            if (decodedToken.email) {
                setEmail(decodedToken.email);
            }
            if (decodedToken.sub) {
                setUserId(decodedToken.sub);
            }
        }
    }, []);

    // Handle tab/window close or navigation away
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            // Only process if user hasn't clicked proceed and isn't in verification state
            if (!verifying && userId && !hasProcessedUnload.current) {
                console.log('handleBeforeUnload');
                AuthService.deleteCognitoUser(userId);
                // Clear local storage
                StorageService.clear();
                
                // Standard event handling for beforeunload
                event.preventDefault();
                event.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        // Also handle the case when user navigates within the app without closing the tab
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            
            // If component unmounts without verifying and cancel/proceed wasn't clicked
            if (!verifying && userId && !hasProcessedUnload.current) {
                AuthService.deleteCognitoUser(userId);
                // Clear local storage
                StorageService.clear();
            }
        };
    }, [userId, verifying]);

    // Modified useEffect for message animation
    useEffect(() => {
        if (!verifying) return;
        
        let messageIndex = 0;
        setCurrentMessageIndex(0);
        setCurrentSubheadingIndex(0);
        
        const showNextMessage = () => {
            setIsAnimatingOut(true);
            setTimeout(() => {
                messageIndex++;
                if (messageIndex >= loadingMessages.length) {
                    // All messages shown, navigate to dashboard
                    navigate('/dashboard?redirect=easywebinar');
                    return;
                }
                
                setCurrentMessageIndex(messageIndex);
                setCurrentSubheadingIndex(messageIndex);
                setIsAnimatingOut(false);
                
                // Schedule next message after 3 seconds
                setTimeout(showNextMessage, 3000);
            }, 500);
        };
        
        // Show first message for 3 seconds, then switch
        const initialTimer = setTimeout(showNextMessage, 3000);
        
        return () => {
            clearTimeout(initialTimer);
        };
    }, [verifying, navigate]);

    const handleCancel = async () => {
        hasProcessedUnload.current = true;
        try {
            // Delete the Cognito user first
            if (userId) {
                await AuthService.deleteCognitoUser(userId);
            }
        } catch (error) {
            console.error('Error deleting user:', error);
        } finally {
            // Navigate to logout page regardless of success or failure
            navigate('/logout');
        }
    };

    // Simplified handleProceed function
    const handleProceed = () => {
        hasProcessedUnload.current = true;
        setVerifying(true);
        setCurrentMessageIndex(0);
        setCurrentSubheadingIndex(0);
    }

    return (
        <Box
            minHeight='100vh'
            display='flex'
            alignItems='center'
            justifyContent='center'
            bgcolor='#F5F6F8'
            px={2}
        >
            <Box
                sx={{
                    background: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0px 10px 25px 0px rgba(0, 0, 0, 0.10)',
                    maxWidth: 500,
                    width: '100%',
                    p: verifying ? { xs: "47px 24px", sm: "47px 61px" } : { xs: "47px 24px", sm: "47px 79px" },
                    textAlign: 'center',
                    animation: `${fadeIn} 0.7s ease`,
                    transition: 'height 0.5s ease'
                }}
            >
                {verifying ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        {/* Simple Spinner */}
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box
                                component="img"
                                src="https://easywebinar-site.s3.us-east-1.amazonaws.com/images/autheticator-app-images/minimal-ew-loader.gif"
                                alt="Loading..."
                                sx={{
                                    width: { xs: '60px', sm: '70px', md: '87px' },
                                    height: { xs: '60px', sm: '70px', md: '87px' }
                                }}
                            />
                        </Box>
                        <Box>
                            <Box
                                sx={{
                                    minHeight: '60px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    fontSize={`min(calc(28px + 4 * (100vw - 400px) / (1920 - 400)), 32px)`}
                                    color={'primary'}
                                    sx={{
                                        fontWeight: 700,
                                        animation: isAnimatingOut ? `${fadeOut} 0.5s ease forwards` : `${fadeIn} 0.5s ease forwards`,
                                        textAlign: 'center',
                                    }}
                                >
                                    {loadingMessages[currentMessageIndex]}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: '450px',
                                    textAlign: 'center',
                                    position: 'relative',
                                    minHeight: '60px'
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    fontSize="min(calc(14px + (16 - 14) * (100vw - 320px) / (1920 - 320)), 16px)"
                                    sx={{
                                        color: '#8a8a8a',
                                        animation: isAnimatingOut ? `${fadeOut} 0.5s ease forwards` : `${fadeIn} 0.5s ease forwards`,
                                        lineHeight: 1.5,
                                        fontWeight: 400
                                    }}
                                >
                                    {subheadings[currentSubheadingIndex]}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Typography
                            variant="h4"
                            fontWeight={700}
                            color="primary"
                            textAlign="center"
                            mb={3}
                            fontSize={`min(calc(28px + 4 * (100vw - 400px) / (1920 - 400)), 32px)`}
                        >
                            Welcome Back!
                        </Typography>
                        <Typography
                            color="text.secondary"
                            fontSize={`min(calc(14px + 2 * (100vw - 400px) / (1920 - 400)), 16px)`}
                            textAlign="center"
                            lineHeight="1.7"
                        >
                            We found an existing account associated with your email:
                            <Box component="span" fontWeight={600} color="text.primary" fontSize={`inherit`}>
                                {" "}{email}
                            </Box>
                        </Typography>
                        <Typography
                            color="text.secondary"
                            fontSize={`min(calc(14px + 2 * (100vw - 400px) / (1920 - 400)), 16px)`}
                            textAlign="center"
                            mb={3}
                            lineHeight="1.7"
                        >
                            Would you like to link this login to your existing dashboard?
                        </Typography>
                        <Box display="flex" gap={"1.5rem"} width="100%" justifyContent="center">
                        <Button
                                variant="outlined"
                                color="primary"
                                size="large"
                                disableElevation
                                sx={{
                                    height: { xs: "40px", sm: "51px" },
                                    fontSize: "16px",
                                    borderWidth: "2px",
                                    px: {
                                        xs: "1.5rem",
                                        sm: "2rem"
                                    }
                                }}
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                disableElevation
                                sx={{
                                    height: { xs: "40px", sm: "51px" },
                                    fontSize: "16px",
                                    px: {
                                        xs: "1.5rem",
                                        sm: "2rem"
                                    }
                                }}
                                onClick={handleProceed}
                            >
                                Proceed
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default LinkAccount;
